
import { useState, useEffect, useRef } from 'react';

type CacheItem<T> = {
  data: T;
  timestamp: number;
  isLoading: boolean;
  error: Error | null;
};

// Cache globale singleton che persiste tra le renderizzazioni dei componenti
const globalCache: { [key: string]: CacheItem<any> } = {};

// Registro dei canali di sottoscrizione di Supabase per evitare duplicazioni
const supabaseChannels: { [key: string]: any } = {};

// Sistema di sottoscrizione per notificare i componenti quando i dati cambiano
type Subscriber = () => void;
const subscribers: { [key: string]: Set<Subscriber> } = {};

/**
 * Hook ottimizzato per gestire una cache globale persistente
 * che si aggiorna solo in risposta a cambiamenti reali nel database
 */
export function useGlobalCache<T>(
  key: string,
  fetchFn: () => Promise<T>,
  options: {
    validity?: number;
    staleTime?: number;
    forceRefresh?: boolean | ((cachedData: T | null) => boolean);
    setupRealtimeSubscription?: (notifyUpdate: () => void) => (() => void);
  } = {}
) {
  const {
    validity = 30 * 60 * 1000, // 30 minuti di default (più lungo)
    staleTime = 5 * 60 * 1000,  // 5 minuti di default
    forceRefresh = false,
    setupRealtimeSubscription
  } = options;

  const [, setForceUpdate] = useState(0);
  const mountedRef = useRef(true);
  const lastFetchRef = useRef<number>(0);

  // Inizializza la cache se non esiste
  if (!globalCache[key]) {
    globalCache[key] = {
      data: null,
      timestamp: 0,
      isLoading: false,
      error: null
    };
  }

  // Inizializza il set di subscriber se non esiste
  if (!subscribers[key]) {
    subscribers[key] = new Set();
  }

  // Funzione per aggiornare la cache
  const updateCache = async (force = false) => {
    const now = Date.now();
    const cacheItem = globalCache[key];
    const dataAge = now - cacheItem.timestamp;

    // Skip se i dati sono freschi e non è richiesto un refresh forzato
    if (!force && dataAge < validity && cacheItem.data !== null) {
      return;
    }

    // Previene chiamate troppo frequenti (throttle)
    if (!force && now - lastFetchRef.current < 2000) {
      return;
    }

    // Aggiorna lo stato di caricamento
    if (!cacheItem.isLoading) {
      cacheItem.isLoading = true;
      // Forza l'aggiornamento della UI solo se cambiamo lo stato di caricamento
      setForceUpdate(prev => prev + 1);
    }

    lastFetchRef.current = now;

    try {
      const data = await fetchFn();
      
      if (mountedRef.current) {
        globalCache[key] = {
          data,
          timestamp: now,
          isLoading: false,
          error: null
        };
        
        // Notifica tutti i subscriber che i dati sono cambiati
        notifySubscribers(key);
      }
    } catch (error) {
      console.error(`Errore nel caricamento dei dati per ${key}:`, error);
      
      if (mountedRef.current) {
        globalCache[key] = {
          ...globalCache[key],
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error))
        };
        
        // Notifica tutti i subscriber che c'è stato un errore
        notifySubscribers(key);
      }
    }
  };

  // Funzione per notificare tutti i subscriber di una chiave
  const notifySubscribers = (cacheKey: string) => {
    if (subscribers[cacheKey]) {
      subscribers[cacheKey].forEach(subscriber => subscriber());
    }
  };

  // Controlla se è necessario forzare l'aggiornamento
  const shouldForceRefresh = () => {
    if (typeof forceRefresh === 'function') {
      return forceRefresh(globalCache[key]?.data);
    }
    return forceRefresh;
  };

  // Effetto per sottoscriversi ai cambiamenti
  useEffect(() => {
    mountedRef.current = true;
    
    // Funzione per aggiornare il componente quando i dati cambiano
    const subscriber = () => {
      if (mountedRef.current) {
        setForceUpdate(prev => prev + 1);
      }
    };
    
    // Aggiungi questo componente ai subscriber per questa chiave
    subscribers[key].add(subscriber);
    
    // Verifica se è necessario un aggiornamento iniziale
    const cacheItem = globalCache[key];
    
    // Carica inizialmente i dati in uno di questi casi:
    // 1. I dati non sono mai stati caricati
    // 2. È richiesto un refresh forzato
    // 3. I dati sono scaduti secondo la validità
    if (
      !cacheItem.data || 
      shouldForceRefresh() || 
      Date.now() - cacheItem.timestamp > validity
    ) {
      updateCache(shouldForceRefresh());
    }

    // Configura la sottoscrizione realtime se fornita
    let cleanupRealtimeSubscription: (() => void) | undefined;
    
    if (setupRealtimeSubscription) {
      cleanupRealtimeSubscription = setupRealtimeSubscription(() => {
        // Quando riceviamo un aggiornamento realtime, aggiorniamo la cache
        updateCache(true);
      });
    }
    
    return () => {
      mountedRef.current = false;
      
      // Rimuovi questo componente dai subscriber
      subscribers[key].delete(subscriber);
      
      // Pulisci la sottoscrizione realtime se esiste
      if (cleanupRealtimeSubscription) {
        cleanupRealtimeSubscription();
      }
    };
  }, [key, validity, setupRealtimeSubscription]);

  // Funzione per forzare l'aggiornamento manuale
  const refresh = () => updateCache(true);

  // Funzione per invalidare la cache
  const invalidate = () => {
    if (globalCache[key]) {
      globalCache[key] = {
        ...globalCache[key],
        timestamp: 0  // Imposta timestamp a 0 per forzare un ricaricamento alla prossima richiesta
      };
      notifySubscribers(key);
    }
  };

  return {
    data: globalCache[key]?.data as T | null,
    isLoading: globalCache[key]?.isLoading || false,
    error: globalCache[key]?.error,
    refresh,
    invalidate
  };
}

// Funzione utility per creare una sottoscrizione Supabase una sola volta per tabella
export function createSupabaseSubscription(
  supabase: any, 
  table: string, 
  filter: any = {},
  onChangeFn: () => void
): () => void {
  // Crea una chiave univoca per questa sottoscrizione
  const channelKey = `${table}-${JSON.stringify(filter)}`;
  
  // Se esiste già una sottoscrizione per questa chiave, incrementa il contatore
  if (!supabaseChannels[channelKey]) {
    // Crea un nuovo canale
    const channel = supabase
      .channel(`db-changes-${channelKey}`)
      .on('postgres_changes', 
        { 
          event: '*', // Ascolta tutti i tipi di eventi (INSERT, UPDATE, DELETE)
          schema: 'public', 
          table: table,
          ...filter
        }, 
        () => {
          console.log(`Ricevuto aggiornamento per ${table}`);
          onChangeFn();
        }
      )
      .subscribe();
    
    supabaseChannels[channelKey] = {
      channel,
      refCount: 1
    };
  } else {
    supabaseChannels[channelKey].refCount++;
  }
  
  // Restituisce una funzione di cleanup
  return () => {
    if (supabaseChannels[channelKey]) {
      supabaseChannels[channelKey].refCount--;
      
      // Se il contatore arriva a 0, rimuovi il canale
      if (supabaseChannels[channelKey].refCount === 0) {
        supabase.removeChannel(supabaseChannels[channelKey].channel);
        delete supabaseChannels[channelKey];
      }
    }
  };
}
